import { AddressUpgrade, UPGRADE_IMPACT_OPTIONS } from 'src/models/address-upgrade.model';

const IMPACT_LABEL: Record<number, string> = UPGRADE_IMPACT_OPTIONS
  .reduce((acc, o) => ({ ...acc, [o.value]: o.viewValue }), {} as Record<number, string>);

const UPGRADE_ICON_KEYWORDS: { test: RegExp; icon: string }[] = [
  { test: /\b(curtain|blackout|blind|shade)/i, icon: 'assets/svg/moon-outline.svg' },
  { test: /\b(charger|charging|outlet|battery)/i, icon: 'assets/svg/battery-charging-outline.svg' },
  { test: /\b(noise|sound|speaker|audio)/i, icon: 'assets/svg/musical-notes-outline.svg' },
  { test: /\b(coffee|cafe|espresso)/i, icon: 'assets/svg/cafe-outline.svg' },
  { test: /\b(string light|patio light|outdoor light|bistro)/i, icon: 'assets/svg/bulb-outline.svg' },
  { test: /\b(lock|keypad|key)/i, icon: 'assets/svg/lock-closed-outline.svg' },
  { test: /\b(mattress|bed|pillow|sheet|linen)/i, icon: 'assets/svg/bed-outline.svg' },
  { test: /\b(fire pit|bonfire|fireplace)/i, icon: 'assets/svg/bonfire-outline.svg' },
  { test: /\b(wi-?fi|wifi|router|mesh|internet)/i, icon: 'assets/svg/wifi-outline.svg' },
  { test: /\b(arcade|game|console|nintendo|xbox|playstation)/i, icon: 'assets/svg/game-controller-outline.svg' },
  { test: /\b(hot tub|jacuzzi|sauna|spa)/i, icon: 'assets/svg/water-outline.svg' },
  { test: /\b(pickleball|tennis|basketball|court|sport)/i, icon: 'assets/svg/basketball-outline.svg' },
  { test: /\b(tv|television|netflix|streaming)/i, icon: 'assets/svg/tv-outline.svg' },
  { test: /\b(grill|bbq|barbecue|smoker)/i, icon: 'assets/svg/flame-outline.svg' },
  { test: /\b(pool|swimming)/i, icon: 'assets/svg/water-outline.svg' },
  { test: /\b(garden|landscaping|plant|flower)/i, icon: 'assets/svg/flower-outline.svg' },
  { test: /\b(car|parking|garage|ev|charger)/i, icon: 'assets/svg/car-outline.svg' },
  { test: /\b(safe|security|alarm|camera)/i, icon: 'assets/svg/shield-outline.svg' },
  { test: /\b(book|reading|library)/i, icon: 'assets/svg/book-outline.svg' },
  { test: /\b(workout|gym|fitness|yoga|treadmill)/i, icon: 'assets/svg/fitness-outline.svg' },
  { test: /\b(paint|repaint|wall|color)/i, icon: 'assets/svg/brush-outline.svg' },
  { test: /\b(decor|art|frame|sign)/i, icon: 'assets/svg/star-outline.svg' },
];

export function getUpgradeImpactLabel(upgrade: AddressUpgrade | null | undefined): string {
  return IMPACT_LABEL[upgrade?.impact_score] || '';
}

export function getUpgradeImpactClass(upgrade: AddressUpgrade | null | undefined): string {
  switch (upgrade?.impact_score) {
    case 3: return 'impact-high';
    case 2: return 'impact-medium';
    case 1: return 'impact-low';
    default: return '';
  }
}

export function getUpgradeIconSrc(upgrade: AddressUpgrade | null | undefined): string {
  const fallback = (() => {
    switch (upgrade?.impact_score) {
      case 3: return 'assets/svg/flame-outline.svg';
      case 2: return 'assets/svg/bulb-outline.svg';
      case 1: return 'assets/svg/construct-outline.svg';
      default: return 'assets/svg/construct-outline.svg';
    }
  })();
  if (!upgrade?.name) {return fallback;}
  for (const k of UPGRADE_ICON_KEYWORDS) {
    if (k.test.test(upgrade.name)) {return k.icon;}
  }
  return fallback;
}

export function getUpgradeCostLabel(upgrade: AddressUpgrade | null | undefined): string {
  const cents = upgrade?.estimated_cost;
  if (cents === null || cents === undefined) {
    return '—';
  }
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export function getUpgradeRoiLabel(upgrade: AddressUpgrade | null | undefined): string {
  const score = upgrade?.roi_score;
  if (score === null || score === undefined) {
    return '—';
  }
  return Number(score).toFixed(1);
}

export function getUpgradeRoiPercent(upgrade: AddressUpgrade | null | undefined): number {
  const score = upgrade?.roi_score ?? 0;
  return Math.max(0, Math.min(100, (score / 10) * 100));
}

export function getUpgradeRequestedDaysAgo(upgrade: AddressUpgrade | null | undefined): string | null {
  const ts = upgrade?.requested_at;
  if (!ts) {
    return null;
  }
  const days = Math.floor((Date.now() - new Date(ts).getTime()) / (1000 * 60 * 60 * 24));
  if (days <= 0) {return 'today';}
  if (days === 1) {return '1d ago';}
  return `${days}d ago`;
}
